import { generatePath, useNavigate } from 'react-router'
import { useShallow } from 'zustand/react/shallow'

import { RouteConfig } from '@ui/interfaces/navigation'
import { RouteNamesEnum, routesConfig } from '@ui/router/routes'
import { useNavigationStateStore } from '@ui/stores/navigation/NavigationStateStore'

type GenerateURLParams = { [key: string]: string | number | null | undefined }

const filterEmptyVariables = (params: GenerateURLParams): Record<string, string> => {
  return (
    Object.entries(params)
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .filter(([_, value]) => value !== null && value !== undefined && value !== '')
      .reduce(
        (acc, [key, value]) => {
          acc[key] = String(value)
          return acc
        },
        {} as Record<string, string>
      )
  )
}

const findRoutePathByName = (name: RouteNamesEnum, routes: RouteConfig[]): string | null => {
  for (const route of routes) {
    if (route.name === name) return route.path || ''
    if (route.children) {
      const childPath: string | null = findRoutePathByName(name, route.children)
      if (childPath) return `${route.path}/${childPath}`
    }
  }
  return null
}

// This hook is used to generate links and navigate to them based on the route name and params
// It uses the useNavigate hook from react-router to navigate to the generated link

/* Example usage:
 const { generateLink, navigateTo } = useAppNavigation()

 ### Navigate with params to a route
 const link = generateLink(RouteNamesEnum.PIPELINE, { pipelineId: selectedPipeline?.id })
 ### Navigate to a route without params, this uses variables set in NavigationStateStore as params
 navigateTo(RouteNamesEnum.PIPELINE) 
 */
export const useAppNavigation = () => {
  // const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { instanceId, pipelineId, nodeId } = useNavigationStateStore(
    useShallow((state) => {
      return {
        // params
        instanceId: state.params.instanceId,
        pipelineId: state.params.pipelineId,
        nodeId: state.params.nodeId,
        // queryParams
        datatype: state.queryParams.datatype,
        groupBy: state.queryParams.groupBy,
        query: state.queryParams.query,
      }
    })
  )

  const generateLink = (
    routeName: RouteNamesEnum,
    params: GenerateURLParams = {},
    queryParams: GenerateURLParams = {}
  ): string => {
    // const searchParamsObject = convertSearchParamsToObject(searchParams)

    const baseParams: GenerateURLParams = Object.keys(params).length > 0 ? params : { instanceId, pipelineId, nodeId }
    const qParams: GenerateURLParams = queryParams

    const routePath = findRoutePathByName(routeName, routesConfig)

    if (!routePath) throw new Error(`Route ${routeName} not found`)

    const filteredBaseParams = filterEmptyVariables(baseParams)
    const filteredQueryParams = filterEmptyVariables(qParams)

    const url = generatePath(routePath, filteredBaseParams)

    const queryString = new URLSearchParams(filteredQueryParams).toString()
    return Object.keys(filteredQueryParams).length > 0 ? `${url}?${queryString}` : url
  }

  const navigateTo = (
    routeName: RouteNamesEnum,
    params: GenerateURLParams = {},
    queryParams: GenerateURLParams = {}
  ) => {
    const url = generateLink(routeName, params, queryParams)
    navigate(url)
  }

  return {
    generateLink,
    navigateTo,
  }
}

export default useAppNavigation
